import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ArrowUpRight, Handshake, Globe, Scales } from "@phosphor-icons/react";
import { Reveal, MaskedLines } from "../components/Reveal";
import { IMG } from "../lib/site";
import { useT } from "../context/LangContext";

const CHAPTER_ICONS = [Globe, Handshake, Scales];

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { t } = useT();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 md:pt-36 pb-6 md:pb-8" data-testid="hero">
      <motion.div style={{ y: yBlob }} className="pointer-events-none absolute -top-24 -right-16 h-[360px] w-[360px] rounded-full bg-ochre/10 blur-3xl" />
      <motion.div style={{ y: yBlob }} className="pointer-events-none absolute top-40 -left-24 h-[320px] w-[320px] rounded-full bg-forest/[0.06] blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <h1 className="font-display font-extrabold tracking-tighter text-ink text-[13vw] leading-[1.08] sm:text-6xl md:text-7xl lg:text-8xl">
          <MaskedLines lines={["The Geography", "of Change."]} delay={0.15} />
        </h1>

        <div className="mt-6 grid gap-8 md:grid-cols-12 items-end">
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="md:col-span-6"
          >
            <p className="text-lg text-stone leading-relaxed">{t.home.heroLead}</p>
            <p className="mt-5 font-serif italic text-xl md:text-2xl text-terracotta leading-snug" data-testid="hero-motto">{t.home.heroMotto}</p>
          </motion.div>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.7 }}
            className="md:col-span-6 md:col-start-7 flex flex-wrap gap-4 md:justify-end"
          >
            <Link to="/sostienici" data-testid="hero-donate-btn" className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 font-semibold text-white transition-colors duration-200 hover:bg-terracotta-dark">
              {t.home.heroDonate}
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link to="/progetti" data-testid="hero-projects-btn" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 font-semibold text-ink transition-colors duration-200 hover:bg-ink hover:text-bone">
              {t.home.heroProjects}
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="mt-14 md:mt-20 relative h-[46vh] md:h-[62vh] overflow-hidden rounded-t-[3rem]"
        >
          <motion.img style={{ y: yImg, scale: 1.15 }} src={IMG.heroUnity} alt="The Geography of Change" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
        </motion.div>
      </div>
    </section>
  );
};

const Manifesto = () => {
  const { t } = useT();
  return (
    <section className="mx-auto max-w-7xl px-5 md:px-8 pt-6 md:pt-8 pb-8 md:pb-10" data-testid="manifesto">
      <Reveal className="max-w-2xl mb-16">
        <span className="font-display font-bold tracking-widest text-xs text-forest">{t.home.manifestoKicker}</span>
        <h2 className="mt-4 font-display font-extrabold tracking-tighter text-4xl md:text-5xl text-ink">{t.home.manifestoTitle}</h2>
      </Reveal>
      <div className="space-y-px">
        {t.home.manifesto.map((c, i) => {
          const Icon = CHAPTER_ICONS[i];
          return (
            <Reveal key={c.n} delay={i * 0.06}>
              <div className="grid gap-6 md:grid-cols-12 items-start border-t border-border py-7 md:py-9">
                <div className="md:col-span-2 font-serif text-6xl md:text-7xl text-ochre leading-none">{c.n}</div>
                <div className="md:col-span-1 pt-2"><Icon size={34} weight="duotone" className="text-terracotta" /></div>
                <h3 className="md:col-span-4 font-display font-bold tracking-tight text-2xl md:text-3xl text-ink">{c.title}</h3>
                <p className="md:col-span-5 text-stone leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const Activities = () => {
  const { t } = useT();
  return (
    <section className="bg-sand" data-testid="activities">
      <div className="mx-auto max-w-7xl px-5 md:px-8 pt-8 md:pt-10 pb-14 md:pb-20">
        <Reveal className="max-w-2xl mb-14">
          <span className="font-display font-bold tracking-widest text-xs text-forest">{t.home.activitiesKicker}</span>
          <h2 className="mt-4 font-display font-extrabold tracking-tighter text-4xl md:text-5xl text-ink">{t.home.activitiesTitle}</h2>
        </Reveal>
        <div className="grid gap-px bg-border md:grid-cols-3 border border-border">
          {t.home.activities.map((a, i) => (
            <Reveal key={a.title} delay={(i % 3) * 0.06} className="bg-bone">
              <div className="group h-full p-8 md:p-10 transition-colors duration-300 hover:bg-surface">
                <div className="font-serif text-2xl text-ochre mb-4">0{i + 1}</div>
                <h3 className="font-display font-bold tracking-tight text-xl text-ink mb-3">{a.title}</h3>
                <p className="text-sm text-stone leading-relaxed">{a.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProjectsPreview = () => {
  const { t } = useT();
  return (
    <section className="mx-auto max-w-7xl px-5 md:px-8 py-14 md:py-20" data-testid="projects-preview">
      <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div className="max-w-2xl">
          <span className="font-display font-bold tracking-widest text-xs text-forest">{t.home.projectsKicker}</span>
          <h2 className="mt-4 font-display font-extrabold tracking-tighter text-4xl md:text-5xl text-ink">{t.home.projectsTitle}</h2>
        </div>
        <Link to="/progetti" className="group inline-flex items-center gap-2 font-semibold text-terracotta" data-testid="projects-preview-all">
          {t.home.projectsAll}
          <ArrowUpRight size={18} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>
      <div className="grid gap-8 md:grid-cols-2">
        {t.projects.items.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <Link to={`/progetti#${p.id}`} className="group block" data-testid={`project-card-${p.id}`}>
              <div className="relative overflow-hidden rounded-2xl aspect-square">
                <img src={p.cover} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
                <span className="absolute top-4 left-4 rounded-full bg-bone/90 px-3 py-1 text-xs font-semibold text-ink">{p.status}</span>
              </div>
              <div className="mt-5">
                <span className="font-display font-bold tracking-widest text-xs text-terracotta">{p.kicker.toUpperCase()}</span>
                <h3 className="mt-2 font-display font-bold tracking-tight text-2xl text-ink">{p.title}</h3>
                <p className="mt-2 text-stone leading-relaxed line-clamp-2">{p.lead}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const DonateCTA = () => {
  const { t } = useT();
  return (
    <section className="bg-forest text-bone" data-testid="donate-cta">
      <div className="mx-auto max-w-7xl px-5 md:px-8 py-14 md:py-20">
        <Reveal>
          <span className="font-display font-bold tracking-widest text-xs text-ochre">{t.home.ctaKicker}</span>
          <h2 className="mt-5 font-display font-extrabold tracking-tighter text-4xl md:text-6xl max-w-4xl">{t.home.ctaTitle}</h2>
          <p className="mt-6 max-w-xl text-bone/80 text-lg leading-relaxed">{t.home.ctaBody}</p>
          <Link to="/sostienici" data-testid="cta-donate-btn" className="mt-10 inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 font-semibold text-white transition-colors duration-200 hover:bg-terracotta-dark">
            {t.home.ctaBtn}
            <ArrowRight size={18} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <Manifesto />
      <Activities />
      <ProjectsPreview />
      <DonateCTA />
    </>
  );
}
